export const JPEG_QUALITY = 0.85;

export type ImageOutputFormat =
  | { mimeType: 'image/png'; quality: undefined }
  | { mimeType: 'image/jpeg'; quality: typeof JPEG_QUALITY };

export function resolveUploadedImageUrl({
  persistedUrl,
  legacyUrl,
}: {
  persistedUrl: string | null | undefined;
  legacyUrl: string;
}): string | null {
  return persistedUrl === undefined ? legacyUrl : persistedUrl;
}

export function hasAlpha(canvas: HTMLCanvasElement): boolean {
  if (canvas.width === 0 || canvas.height === 0) {
    return false;
  }

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Image canvas is unavailable');
  }

  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let alphaIndex = 3; alphaIndex < pixels.length; alphaIndex += 4) {
    if (pixels[alphaIndex] < 255) {
      return true;
    }
  }

  return false;
}

/**
 * Every upload is encoded as PNG, logos and campaign images alike.
 *
 * The images service keys the stored original by the format it is sent
 * (`{type}/{name}.png` or `.jpg`), but no record persists which extension an
 * image was stored under — only `imageVersion` / `logoVersion`. Every reader
 * of an original therefore synthesizes `.png`: the donor pages
 * (EmbedFormDetailPage, EventDetailPage, HeaderImage's non-variant fallback),
 * hosted-page meta tags, the contact-portal cards, the dashboard's own edit
 * drawers, and the messaging/email/accounts logo consumers. A JPEG original
 * would land at `.jpg`, be unreachable from all of them, and — on a form that
 * already had an image — leave the PREVIOUS `.png` in place, so donors would
 * see the replaced image under the new version token.
 *
 * Returning to JPEG originals needs the record to carry the stored extension
 * (an `imageFormat` persisted beside `imageVersion`, served on the donor APIs)
 * and every reader above to pass it to `imageUrl({ ext })`. Until then the
 * CON-70 byte savings come from the backend's webp/jpeg width variants, which
 * are keyed independently of this choice.
 */
export function chooseOutputFormat(_params: {
  imageType: string | null | undefined;
  hasAlpha: boolean;
}): ImageOutputFormat {
  return { mimeType: 'image/png', quality: undefined };
}
